import React, { useState } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    Box,
    Typography,
    IconButton,
    TextField,
    InputAdornment,
    Grid,
    Card,
    CardActionArea,
    Fade,
    useTheme,
    useMediaQuery,
    Container,
} from '@mui/material';
import {
    Close as CloseIcon,
    Search as SearchIcon,
    Translate as TranslateIcon,
} from '@mui/icons-material';

export interface Language {
    code: string;
    name: string;
    flag: string | any;
    isImage?: boolean;
}

export interface LanguageModalProps {
    open: boolean;
    onClose: () => void;
    onSelect: (lang: Language) => void;
    title: string;
    selectedCode?: string;
    languages?: Language[];
}

export const TOP_LANGUAGES: Language[] = [
    { code: 'en', name: 'English', flag: '🇺🇸' },
    { code: 'fr', name: 'Français (French)', flag: '🇫🇷' },
    { code: 'es', name: 'Español (Spanish)', flag: '🇪🇸' },
    { code: 'fa', name: 'فارسی (Farsi)', flag: '/assets/images/iran_lion_sun.png', isImage: true },
    { code: 'pa', name: 'ਪੰਜਾਬੀ (Punjabi)', flag: '🇮🇳' },
    { code: 'ar', name: 'العربية (Arabic)', flag: '🇸🇦' },
    { code: 'zh-CN', name: '简体中文 (Simplified Chinese)', flag: '🇨🇳' },
    { code: 'zh-TW', name: '繁體中文 (Traditional Chinese)', flag: '🇹🇼' },
];

export const MORE_LANGUAGES: Language[] = [
    { code: 'ur', name: 'اردو (Urdu)', flag: '🇵🇰' },
    { code: 'dari', name: 'دری (Dari)', flag: '🇦🇫' },
    { code: 'hi', name: 'हिन्दी (Hindi)', flag: '🇮🇳' },
    { code: 'tl', name: 'Tagalog (Philippines)', flag: '🇵🇭' },
    { code: 'it', name: 'Italiano (Italian)', flag: '🇮🇹' },
    { code: 'de', name: 'Deutsch (German)', flag: '🇩🇪' },
    { code: 'pt', name: 'Português (Portuguese)', flag: '🇵🇹' },
    { code: 'ru', name: 'Русский (Russian)', flag: '🇷🇺' },
    { code: 'ja', name: '日本語 (Japanese)', flag: '🇯🇵' },
    { code: 'ko', name: '한국어 (Korean)', flag: '🇰🇷' },
    { code: 'vi', name: 'Tiếng Việt (Vietnamese)', flag: '🇻🇳' },
    { code: 'he', name: 'עברית (Hebrew)', flag: '🇮🇱' },
    { code: 'tr', name: 'Türkçe (Turkish)', flag: '🇹🇷' },
    { code: 'nl', name: 'Nederlands (Dutch)', flag: '🇳🇱' },
    { code: 'pl', name: 'Polski (Polish)', flag: '🇵🇱' },
    { code: 'id', name: 'Bahasa Indonesia (Indonesian)', flag: '🇮🇩' },
    { code: 'el', name: 'Ελληνικά (Greek)', flag: '🇬🇷' },
];

const ALL_LANGS = [...TOP_LANGUAGES, ...MORE_LANGUAGES];

const LanguageModal: React.FC<LanguageModalProps> = ({
    open,
    onClose,
    onSelect,
    title,
    selectedCode,
    languages,
}) => {
    const [searchQuery, setSearchQuery] = useState('');
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    const displayLangs = languages || ALL_LANGS;

    const filteredLangs = displayLangs.filter(
        (l) =>
            l.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
            l.code.toLowerCase().includes(searchQuery.toLowerCase())
    );

    const handleSelect = (lang: Language) => {
        onSelect(lang);
        onClose();
    };

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="md"
            fullWidth
            fullScreen={isMobile}
            PaperProps={{
                sx: {
                    borderRadius: isMobile ? 0 : '32px',
                    background: 'rgba(255, 255, 255, 0.9)',
                    backdropFilter: 'blur(20px)',
                    boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
                    overflow: 'hidden',
                    minHeight: '80vh',
                },
            }}
        >
            <DialogTitle sx={{ p: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <TranslateIcon sx={{ color: 'primary.main', fontSize: '2.5rem' }} />
                    <Typography variant="h3" fontWeight="900" sx={{ letterSpacing: '-0.02em' }}>
                        {title}
                    </Typography>
                </Box>
                <IconButton onClick={onClose} sx={{ bgcolor: 'rgba(0,0,0,0.05)', '&:hover': { bgcolor: 'rgba(0,0,0,0.1)' } }}>
                    <CloseIcon />
                </IconButton>
            </DialogTitle>

            <DialogContent sx={{ px: 4, pb: 6 }}>
                {/* Search Bar */}
                <Box sx={{ mb: 4 }}>
                    <TextField
                        fullWidth
                        placeholder="Search language..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon sx={{ color: 'primary.main', ml: 1, mr: 1 }} />
                                </InputAdornment>
                            ),
                            sx: {
                                borderRadius: '20px',
                                fontSize: '1.2rem',
                                backgroundColor: 'rgba(0,0,0,0.02)',
                                '& fieldset': { border: 'none' },
                                border: '1px solid rgba(0,0,0,0.05)',
                                boxShadow: '0 4px 12px rgba(0,0,0,0.02)',
                            },
                        }}
                    />
                </Box>

                <Container maxWidth="lg" sx={{ p: 0 }}>
                    <Grid container spacing={2}>
                        {filteredLangs.map((lang, index) => (
                            <Grid size={{ xs: 12, sm: 6, md: 4 }} key={lang.code}>
                                <Fade in timeout={300 + index * 30}>
                                    <Card
                                        sx={{
                                            borderRadius: '20px',
                                            border: '2px solid',
                                            borderColor: selectedCode === lang.code ? 'primary.main' : 'transparent',
                                            background: selectedCode === lang.code ? 'rgba(16, 185, 129, 0.05)' : 'white',
                                            transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                                            '&:hover': {
                                                transform: 'translateY(-4px)',
                                                borderColor: 'primary.light',
                                                boxShadow: '0 12px 20px -8px rgba(16, 185, 129, 0.3)',
                                            },
                                        }}
                                    >
                                        <CardActionArea onClick={() => handleSelect(lang)} sx={{ p: 2 }}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                                {lang.isImage ? (
                                                    <Box
                                                        component="img"
                                                        src={lang.flag}
                                                        sx={{ width: 64, height: 48, borderRadius: '6px', objectFit: 'cover', boxShadow: '0 4px 8px rgba(0,0,0,0.1)' }}
                                                    />
                                                ) : (
                                                    <Typography sx={{ fontSize: '4rem' }}>{lang.flag}</Typography>
                                                )}
                                                <Box>
                                                    <Typography variant="h4" fontWeight="900" sx={{ color: 'text.primary', mb: 0.5 }}>
                                                        {lang.name}
                                                    </Typography>
                                                    <Typography variant="body1" sx={{ color: 'text.secondary', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '1px' }}>
                                                        {lang.code}
                                                    </Typography>
                                                </Box>
                                            </Box>
                                        </CardActionArea>
                                    </Card>
                                </Fade>
                            </Grid>
                        ))}
                    </Grid>
                </Container>

                {filteredLangs.length === 0 && (
                    <Box sx={{ textAlign: 'center', py: 8 }}>
                        <Typography variant="h2" sx={{ mb: 2 }}>🤔</Typography>
                        <Typography variant="h4" fontWeight="900" sx={{ mb: 1 }}>
                            Language not found
                        </Typography>
                        <Typography variant="body1" color="text.secondary">
                            Try searching for something else
                        </Typography>
                    </Box>
                )}
            </DialogContent>
        </Dialog>
    );
};

export default LanguageModal;
